import React, { useState } from 'react';
import { Link, useNavigate, NavLink } from 'react-router-dom';
import { Search, MapPin, ShoppingCart, User, LogOut, LayoutDashboard, ChevronDown } from 'lucide-react';
import { DISTRICTS } from '../../data/mockData';
import { useApp } from '../../context/AppContext';
import { useAuth, homeForRole } from '../../context/AuthContext';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { InstallButton } from './InstallButton';
import { Logo } from './Logo';

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/marketplace', label: 'Marketplace' },
  { to: '/promotions', label: 'Promotions' },
  { to: '/made-in-kelantan', label: 'Made in Kelantan' },
  { to: '/explore', label: 'Explore Kelantan' },
  { to: '/partners', label: 'Partners' },
];

export function PublicHeader() {
  const navigate = useNavigate();
  const { cart } = useApp();
  const { user, logout } = useAuth();
  const [q, setQ] = useState('');
  const [location, setLocation] = useState(() => localStorage.getItem('kelate_location') || 'Kota Bharu');
  const cartCount = cart.reduce((sum, c) => sum + c.qty, 0);

  const submitSearch = (e) => {
    e.preventDefault();
    navigate(q.trim() ? `/search?q=${encodeURIComponent(q.trim())}` : '/search');
  };

  const pickLocation = (d) => {
    setLocation(d);
    localStorage.setItem('kelate_location', d);
  };

  return (
    <header data-testid="public-header" className="sticky top-0 z-40 border-b border-kelate-line bg-kelate-bg/95 backdrop-blur">
      <div className="mx-auto max-w-[1280px] px-4">
        <div className="flex h-16 items-center gap-4">
          <Link to="/" data-testid="logo-link" aria-label="KELATE home">
            <Logo className="h-8" />
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) => `rounded-sm px-3 py-1.5 text-sm transition-colors ${isActive ? 'bg-kelate-darkwood text-kelate-surface' : 'text-kelate-soft hover:text-kelate-ink hover:bg-secondary'}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <form onSubmit={submitSearch} className="ml-auto hidden md:flex flex-1 max-w-md items-center">
            <div className="relative w-full">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-kelate-soft" />
              <input
                data-testid="header-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search products, stores or services..."
                className="h-9 w-full rounded-sm border border-kelate-line bg-kelate-surface pl-9 pr-3 text-sm outline-none placeholder:text-kelate-soft focus:border-kelate-wood"
              />
            </div>
          </form>
          <div className="flex items-center gap-1">
            <Popover>
              <PopoverTrigger asChild>
                <button data-testid="location-selector" className="hidden sm:flex items-center gap-1 rounded-sm px-2 py-1.5 text-xs text-kelate-soft hover:bg-secondary">
                  <MapPin size={14} className="text-kelate-wood" />
                  <span className="max-w-[90px] truncate">{location}</span>
                  <ChevronDown size={12} />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-48 p-1 bg-kelate-surface border-kelate-line" align="end">
                {DISTRICTS.map((d) => (
                  <button
                    key={d}
                    data-testid={`location-${d.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => pickLocation(d)}
                    className={`w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-secondary ${d === location ? 'font-semibold text-kelate-darkwood' : 'text-kelate-ink'}`}
                  >
                    {d}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
            <Link to="/cart" data-testid="cart-link" className="relative rounded-sm p-2 text-kelate-ink hover:bg-secondary">
              <ShoppingCart size={18} />
              {cartCount > 0 && (
                <span data-testid="cart-badge" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-kelate-red px-1 text-[10px] font-semibold text-white">
                  {cartCount}
                </span>
              )}
            </Link>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="account-menu" className="flex items-center gap-1.5 rounded-sm px-2 py-1.5 text-sm text-kelate-ink hover:bg-secondary">
                    <User size={16} />
                    <span className="hidden sm:inline max-w-[80px] truncate">{user.name?.split(' ')[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-52 bg-kelate-surface border-kelate-line">
                  <DropdownMenuItem onClick={() => navigate('/my-kelate')} data-testid="menu-my-kelate">My KELATE</DropdownMenuItem>
                  {['merchant', 'admin', 'rider'].includes(user.role) && (
                    <DropdownMenuItem onClick={() => navigate(homeForRole(user.role))} data-testid="menu-workspace">
                      <LayoutDashboard size={14} className="mr-2" />
                      {user.role === 'merchant' ? 'Merchant Center' : user.role === 'admin' ? 'Admin Center' : 'Delivery Jobs'}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={async () => { await logout(); navigate('/'); }} data-testid="menu-logout">
                    <LogOut size={14} className="mr-2" /> Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <span className="flex flex-col items-stretch gap-1">
                <Link to="/account/login" data-testid="login-link" className="rounded-sm border border-kelate-darkwood px-3 py-1.5 text-center text-sm font-medium text-kelate-darkwood hover:bg-kelate-darkwood hover:text-kelate-surface">
                  Log in
                </Link>
                <InstallButton testId="header-install-app-btn" className="w-full py-1 text-xs" />
              </span>
            )}
          </div>
        </div>
        <form onSubmit={submitSearch} className="pb-3 md:hidden">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-kelate-soft" />
            <input
              data-testid="header-search-input-mobile"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search products, stores or services..."
              className="h-9 w-full rounded-sm border border-kelate-line bg-kelate-surface pl-9 pr-3 text-sm outline-none placeholder:text-kelate-soft focus:border-kelate-wood"
            />
          </div>
        </form>
      </div>
    </header>
  );
}
